import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData';
import { showToast } from '@/components/layout/Toast';
import { exportToExcel } from '@/lib/utils';
import type { NewsletterSuscriptor } from '@/lib/types';
import { PageHeader } from '@/components/ui';

/* Ver la nota de CampanasEmail: el relleno y su texto van juntos. */
type ColorBadge = { bg: string; fg: string };
const BADGE_NEUTRO: ColorBadge = { bg: 'var(--text-muted)', fg: 'var(--text-on-primary)' };
const estadoColors: Record<string, ColorBadge> = {
  activo: { bg: 'var(--success)', fg: 'var(--on-success)' },
  baja: { bg: 'var(--warning)', fg: 'var(--on-warning)' },
};

const origenColors: Record<string, ColorBadge> = {
  blog: { bg: 'var(--brand-500)', fg: 'var(--text-on-primary)' },
  landing: { bg: 'var(--primary-light)', fg: 'var(--text-on-primary)' },
  manual: { bg: 'var(--info)', fg: 'var(--on-info)' },
  import: BADGE_NEUTRO,
};

interface ListResponse {
  suscriptores: NewsletterSuscriptor[];
  total: number;
  page: number;
  limit: number;
}

export default function NewsletterSuscriptores() {
  const api = useApi();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Filtros
  const [estado, setEstado] = useState('');
  const [origen, setOrigen] = useState('');
  const [q, setQ] = useState('');

  // ── Fetch ──
  // Los filtros van en la CLAVE del caché: cada combinación se guarda aparte,
  // así que volver a un filtro ya visto es instantáneo. La búsqueda escrita se
  // aplica aparte (`qAplicado`) y no mientras se tipea — con la `q` en la clave,
  // cada letra sería una consulta.
  const [qAplicado, setQAplicado] = useState('');
  const { data, isLoading: loading, refrescar, actualizar } = useCachedData(
    ['newsletter', estado, origen, qAplicado],
    () => {
      const params = new URLSearchParams({ limit: '500' });
      if (estado) params.set('estado', estado);
      if (origen) params.set('origen', origen);
      if (qAplicado) params.set('q', qAplicado);
      return api.get<ListResponse>(`/api/newsletter?${params.toString()}`).catch(() => {
        showToast('Error al cargar suscriptores', 'error');
        return { suscriptores: [], total: 0, page: 1, limit: 500 } as ListResponse;
      });
    },
  );
  const rows = data?.suscriptores ?? [];
  const total = data?.total ?? 0;

  const fetchRows = () => { setSelected(new Set()); setQAplicado(q.trim()); void refrescar(); };

  // Cambiar de filtro cambia la lista, así que la selección de antes ya no
  // apunta a estas filas.
  useEffect(() => { setSelected(new Set()); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [estado, origen, qAplicado]);

  // ── Contadores ──
  const altasDelMes = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return rows.filter((r) => new Date(r.created_at) >= monthStart).length;
  }, [rows]);

  const activos = useMemo(() => rows.filter((r) => r.estado === 'activo').length, [rows]);

  // ── Acciones ──
  const toggleEstado = async (r: NewsletterSuscriptor) => {
    const nuevo: 'activo' | 'baja' = r.estado === 'activo' ? 'baja' : 'activo';
    try {
      await api.request(`/api/newsletter/${r.id}`, { method: 'PATCH', body: { estado: nuevo } });
      actualizar((prev) => ({ ...prev, suscriptores: prev.suscriptores.map((x) => (x.id === r.id ? { ...x, estado: nuevo } : x)) }));
      showToast(nuevo === 'baja' ? 'Suscriptor dado de baja' : 'Suscriptor reactivado', 'success');
    } catch {
      showToast('Error al actualizar estado', 'error');
    }
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const handleExport = () => {
    const data = rows.map((r) => ({
      Email: r.email,
      Nombre: r.nombre || '',
      Origen: r.origen,
      'Fuente URL': r.fuente_url || '',
      Interés: r.interes || '',
      Estado: r.estado,
      'Fecha alta': new Date(r.created_at).toLocaleDateString('es-PE'),
    }));
    if (data.length === 0) {
      showToast('No hay suscriptores para exportar', 'info');
      return;
    }
    exportToExcel(data, `Suscriptores-${new Date().toISOString().slice(0, 10)}`, 'Suscriptores');
  };

  const handleCrearCampana = async () => {
    const ids = Array.from(selected);
    const cuenta = ids.length > 0 ? ids.length : activos;
    if (cuenta === 0) {
      showToast('No hay suscriptores activos para la campaña', 'info');
      return;
    }
    const nombre = window.prompt(
      `Crear campaña con ${cuenta} suscriptor(es) ${ids.length > 0 ? 'seleccionados' : 'activos'}.\nNombre de la campaña:`,
      `Newsletter ${new Date().toISOString().slice(0, 10)}`
    );
    if (nombre === null) return;
    try {
      const res = await api.post<{ campana_id: number; insertados: number; omitidos: number }>(
        '/api/newsletter/a-campana',
        { nombre: nombre.trim() || undefined, ids: ids.length > 0 ? ids : undefined }
      );
      showToast(`Campaña creada · ${res.insertados} añadidos${res.omitidos ? `, ${res.omitidos} omitidos` : ''}`, 'success');
      navigate('/marketing/email');
    } catch {
      showToast('Error al crear la campaña', 'error');
    }
  };

  // ── Estilos ──
  const page: React.CSSProperties = { padding: 32, maxWidth: 1200, margin: '0 auto' };
  const selectStyle: React.CSSProperties = {
    padding: '9px 12px',
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text-primary)',
    fontSize: 14,
    outline: 'none',
  };
  const inputStyle: React.CSSProperties = { ...selectStyle, minWidth: 220 };
  const btnPrimary: React.CSSProperties = {
    padding: '10px 20px', background: 'var(--brand-500)', color: 'var(--text-on-primary)', border: 'none',
    borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  };
  const btnGhost: React.CSSProperties = {
    padding: '10px 20px', background: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, cursor: 'pointer',
  };
  const card: React.CSSProperties = {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '18px 22px', flex: 1,
  };
  const th: React.CSSProperties = {
    textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)',
    borderBottom: '1px solid var(--border)',
  };
  const td: React.CSSProperties = {
    padding: '12px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)',
  };
  const badge = (c: ColorBadge): React.CSSProperties => ({
    display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 11,
    fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
    background: c.bg, color: c.fg,
  });

  return (
    <div style={page}>
      <PageHeader
        breadcrumbs={[{ label: 'Marketing' }, { label: 'Newsletter' }]}
        meta={`${total} suscriptor${total !== 1 ? 'es' : ''}`}
        actions={
          <>
            <button style={btnGhost} onClick={handleExport}>Exportar</button>
            <button style={btnPrimary} onClick={handleCrearCampana}>
              Crear campaña con esta lista
            </button>
          </>
        }
      />

      {/* Contadores */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={card}>
          <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--text-primary)' }}>{total}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Total suscriptores</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--success)' }}>{activos}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Activos (visibles)</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--brand-500)' }}>{altasDelMes}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Altas este mes (visibles)</div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          style={inputStyle}
          placeholder="Buscar por email o nombre…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') fetchRows(); }}
        />
        <select style={selectStyle} value={estado} onChange={(e) => setEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="activo">Activo</option>
          <option value="baja">Baja</option>
        </select>
        <select style={selectStyle} value={origen} onChange={(e) => setOrigen(e.target.value)}>
          <option value="">Todos los orígenes</option>
          <option value="blog">Blog</option>
          <option value="landing">Landing</option>
          <option value="manual">Manual</option>
          <option value="import">Import</option>
        </select>
        <button style={btnGhost} onClick={fetchRows}>Buscar</button>
      </div>

      {/* Tabla */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 40 }}>
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th style={th}>Email</th>
              <th style={th}>Nombre</th>
              <th style={th}>Origen</th>
              <th style={th}>Fuente</th>
              <th style={th}>Estado</th>
              <th style={th}>Alta</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={{ ...td, textAlign: 'center', padding: 40 }} colSpan={8}>Cargando…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--text-muted)' }} colSpan={8}>
                Sin suscriptores todavía.
              </td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td style={td}>
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} />
                  </td>
                  <td style={td}>{r.email}</td>
                  <td style={td}>{r.nombre || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={td}><span style={badge(origenColors[r.origen] || BADGE_NEUTRO)}>{r.origen}</span></td>
                  <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.fuente_url ? <span title={r.fuente_url} style={{ opacity: 0.7 }}>{r.fuente_url}</span> : <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                  <td style={td}><span style={badge(estadoColors[r.estado] || BADGE_NEUTRO)}>{r.estado}</span></td>
                  <td style={td}>{new Date(r.created_at).toLocaleDateString('es-PE')}</td>
                  <td style={td}>
                    <button style={{ ...btnGhost, padding: '6px 12px', fontSize: 13 }} onClick={() => toggleEstado(r)}>
                      {r.estado === 'activo' ? 'Dar de baja' : 'Reactivar'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
